import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";

import {
  createOrganizationCapitalGoal,
  fetchMemberTypes,
  fetchPartnerTypes,
  fetchBusinessDomains,
  fetchStrategicGoals,
  fetchCustomerValues,
  fetchFinancialGoals,
  fetchOrgCapitalGoals,
  fetchStrategicIssues,
  fetchHumanCapitalGoals,
  fetchInfoCapitalGoalsApp,
  fetchInfoCapitalGoalsIt,
  fetchInfoCapitalGoalsData,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { useAuth } from "../../providers/AuthProvider";

export default function SmOrgCapitalGoalCreatePage() {
  const { businessDomainId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const { companyId } = useAuth();
  const t = (id) => intl.formatMessage({ id });

  const [successFactorName, setSuccessFactorName] = useState("");
  const [successFactorDescription, setSuccessFactorDescription] = useState("");
  const [selectedMemberType, setSelectedMemberType] = useState(null);
  const [selectedPartnerType, setSelectedPartnerType] = useState(null);
  const [selectedNames, setSelectedNames] = useState([]);
  const [sourceIds, setSourceIds] = useState([]);
  const [memberTypeGroups, setMemberTypeGroups] = useState([]); // [{ domainName, types[] }]
  const [partnerTypeGroups, setPartnerTypeGroups] = useState([]); // [{ domainName, types[] }]
  const [strategicGoals, setStrategicGoals] = useState([]);
  const [customerValues, setCustomerValues] = useState([]);
  const [financialGoals, setFinancialGoals] = useState([]);
  const [strategicIssues, setStrategicIssues] = useState([]);
  const [humanCapitalGoals, setHumanCapitalGoals] = useState([]);
  const [infoCapitalGoalsApp, setInfoCapitalGoalsApp] = useState([]);
  const [infoCapitalGoalsIt, setInfoCapitalGoalsIt] = useState([]);
  const [infoCapitalGoalsData, setInfoCapitalGoalsData] = useState([]);
  const [usedIndicatorNames, setUsedIndicatorNames] = useState(new Set());

  // 選択されたメンバータイプまたはパートナータイプの indicators から取得
  const sourceIndicators = selectedMemberType?.indicators || selectedPartnerType?.indicators || [];

  // Selectの値（mt:uuid / pt:uuid / ""）
  const selectedTypeValue = selectedMemberType
    ? `mt:${selectedMemberType.memberTypeId}`
    : selectedPartnerType
      ? `pt:${selectedPartnerType.partnerTypeId}`
      : "";

  useEffect(() => {
    (async () => {
      try {
        const [domains, sgGoals, cvGoals, fgGoals, existingSfs, siGoals, hcGoals, icApp, icIt, icData] = await Promise.all([
          fetchBusinessDomains(companyId).catch(() => []),
          fetchStrategicGoals(businessDomainId).catch(() => []),
          fetchCustomerValues(businessDomainId).catch(() => []),
          fetchFinancialGoals(businessDomainId).catch(() => []),
          fetchOrgCapitalGoals(businessDomainId).catch(() => []),
          fetchStrategicIssues(businessDomainId).catch(() => []),
          fetchHumanCapitalGoals(businessDomainId).catch(() => []),
          fetchInfoCapitalGoalsApp(businessDomainId).catch(() => []),
          fetchInfoCapitalGoalsIt(businessDomainId).catch(() => []),
          fetchInfoCapitalGoalsData(businessDomainId).catch(() => []),
        ]);
        // 全事業ドメインのメンバータイプ・パートナータイプを並列取得
        const domainList = Array.isArray(domains) ? domains : [];
        const [mtypeResults, ptypeResults] = await Promise.all([
          Promise.all(domainList.map((d) => fetchMemberTypes(d.businessDomainId).catch(() => []))),
          Promise.all(domainList.map((d) => fetchPartnerTypes(d.businessDomainId).catch(() => []))),
        ]);
        const mGroups = domainList
          .map((d, i) => ({ domainName: d.businessDomainName, types: Array.isArray(mtypeResults[i]) ? mtypeResults[i] : [] }))
          .filter((g) => g.types.length > 0);
        const pGroups = domainList
          .map((d, i) => ({ domainName: d.businessDomainName, types: Array.isArray(ptypeResults[i]) ? ptypeResults[i] : [] }))
          .filter((g) => g.types.length > 0);
        setMemberTypeGroups(mGroups);
        setPartnerTypeGroups(pGroups);
        setStrategicGoals(Array.isArray(sgGoals) ? sgGoals : []);
        setCustomerValues(Array.isArray(cvGoals) ? cvGoals : []);
        setFinancialGoals(Array.isArray(fgGoals) ? fgGoals : []);
        setStrategicIssues(Array.isArray(siGoals) ? siGoals : []);
        setHumanCapitalGoals(Array.isArray(hcGoals) ? hcGoals : []);
        setInfoCapitalGoalsApp(Array.isArray(icApp) ? icApp : []);
        setInfoCapitalGoalsIt(Array.isArray(icIt) ? icIt : []);
        setInfoCapitalGoalsData(Array.isArray(icData) ? icData : []);
        const used = new Set(
          (Array.isArray(existingSfs) ? existingSfs : [])
            .flatMap((sf) => sf.indicators || [])
            .map((ind) => ind.indicatorName)
            .filter(Boolean)
        );
        setUsedIndicatorNames(used);
      } catch (e) {
        notify.apiError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId]);

  const handleTypeChange = (e) => {
    const val = e.target.value;
    setSelectedNames([]);
    if (!val) {
      setSelectedMemberType(null);
      setSelectedPartnerType(null);
    } else if (val.startsWith("mt:")) {
      const id = val.slice(3);
      const allMemberTypes = memberTypeGroups.flatMap((g) => g.types);
      setSelectedMemberType(allMemberTypes.find((mt) => mt.memberTypeId === id) || null);
      setSelectedPartnerType(null);
    } else if (val.startsWith("pt:")) {
      const id = val.slice(3);
      const allPartnerTypes = partnerTypeGroups.flatMap((g) => g.types);
      setSelectedPartnerType(allPartnerTypes.find((pt) => pt.partnerTypeId === id) || null);
      setSelectedMemberType(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sourceIds.length === 0) {
      notify.error(t("strategicGoal.sourceRequired"));
      return;
    }
    if (sourceIndicators.length > 0 && selectedNames.length === 0) {
      notify.error(t("financialGoal.indicatorRequired"));
      return;
    }
    try {
      const indicators = selectedNames
        .map((name) => sourceIndicators.find((ind) => ind.indicatorName === name))
        .filter(Boolean);
      const allGoals = [...strategicGoals, ...customerValues, ...financialGoals, ...strategicIssues, ...humanCapitalGoals, ...infoCapitalGoalsApp, ...infoCapitalGoalsIt, ...infoCapitalGoalsData];
      const sources = sourceIds
        .map((id) => {
          const goal = allGoals.find((s) => s.successFactorId === id);
          if (!goal) return null;
          return { successFactorId: goal.successFactorId, successFactorName: goal.successFactorName };
        })
        .filter(Boolean);
      await createOrganizationCapitalGoal({
        successFactorName,
        successFactorDescription,
        memberTypeId: selectedMemberType?.memberTypeId || "",
        partnerTypeId: selectedPartnerType?.partnerTypeId || "",
        indicators,
        sources,
        businessDomainId,
        companyId,
      });
      notify.success(t("message.create.success"));
      navigate(`/design/${businessDomainId}/strategy-map/org-capital-goals`, { replace: true });
    } catch (err) {
      notify.apiError(err);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("orgCapitalGoal.create.title")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              fullWidth required
              label={t("successFactor.name")}
              value={successFactorName}
              onChange={(e) => setSuccessFactorName(e.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth multiline rows={3}
              label={t("successFactor.description")}
              value={successFactorDescription}
              onChange={(e) => setSuccessFactorDescription(e.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("orgCapitalGoal.kpiType.label")}</InputLabel>
              <Select
                value={selectedTypeValue}
                label={t("orgCapitalGoal.kpiType.label")}
                onChange={handleTypeChange}
              >
                <MenuItem value=""><em>—</em></MenuItem>
                {memberTypeGroups.length > 0 && [
                  <MenuItem key="__mt_section" disabled sx={{ fontWeight: 700, opacity: 1, color: "primary.main" }}>
                    ■ {t("memberType.label")}
                  </MenuItem>,
                  ...memberTypeGroups.flatMap((group) => [
                    <MenuItem key={`__mthd_${group.domainName}`} disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary", pl: 3 }}>
                      ── {group.domainName} ──
                    </MenuItem>,
                    ...group.types.map((mt) => (
                      <MenuItem key={`mt:${mt.memberTypeId}`} value={`mt:${mt.memberTypeId}`} sx={{ pl: 4 }}>
                        {mt.memberTypeName}
                      </MenuItem>
                    )),
                  ]),
                ]}
                {partnerTypeGroups.length > 0 && [
                  <MenuItem key="__pt_section" disabled sx={{ fontWeight: 700, opacity: 1, color: "primary.main" }}>
                    ■ {t("partnerType.title")}
                  </MenuItem>,
                  ...partnerTypeGroups.flatMap((group) => [
                    <MenuItem key={`__pthd_${group.domainName}`} disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary", pl: 3 }}>
                      ── {group.domainName} ──
                    </MenuItem>,
                    ...group.types.map((pt) => (
                      <MenuItem key={`pt:${pt.partnerTypeId}`} value={`pt:${pt.partnerTypeId}`} sx={{ pl: 4 }}>
                        {pt.partnerTypeName}
                      </MenuItem>
                    )),
                  ]),
                ]}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("orgCapitalGoal.sources.label")}</InputLabel>
              <Select
                multiple
                value={sourceIds}
                onChange={(e) => setSourceIds(e.target.value)}
                input={<OutlinedInput label={t("orgCapitalGoal.sources.label")} />}
              >
                {financialGoals.length > 0 && [
                  <MenuItem key="__fg_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                    ── {t("financialGoal.list.title")} ──
                  </MenuItem>,
                  ...financialGoals.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>
                      {item.successFactorName}
                    </MenuItem>
                  )),
                ]}
                {customerValues.length > 0 && [
                  <MenuItem key="__cv_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                    ── {t("customerValue.list.title")} ──
                  </MenuItem>,
                  ...customerValues.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>
                      {item.successFactorName}
                    </MenuItem>
                  )),
                ]}
                {strategicGoals.length > 0 && [
                  <MenuItem key="__sg_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                    ── {t("strategicGoal.list.title")} ──
                  </MenuItem>,
                  ...strategicGoals.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>
                      {item.successFactorName}
                    </MenuItem>
                  )),
                ]}
                {strategicIssues.length > 0 && [
                  <MenuItem key="__si_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                    ── {t("strategicIssue.list.title")} ──
                  </MenuItem>,
                  ...strategicIssues.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>
                      {item.successFactorName}
                    </MenuItem>
                  )),
                ]}
                {humanCapitalGoals.length > 0 && [
                  <MenuItem key="__hcg_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                    ── {t("humanCapitalGoal.list.title")} ──
                  </MenuItem>,
                  ...humanCapitalGoals.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>
                      {item.successFactorName}
                    </MenuItem>
                  )),
                ]}
                {infoCapitalGoalsApp.length > 0 && [
                  <MenuItem key="__ica_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                    ── {t("infoCapitalGoalApp.list.title")} ──
                  </MenuItem>,
                  ...infoCapitalGoalsApp.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>
                      {item.successFactorName}
                    </MenuItem>
                  )),
                ]}
                {infoCapitalGoalsIt.length > 0 && [
                  <MenuItem key="__icit_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                    ── {t("infoCapitalGoalIt.list.title")} ──
                  </MenuItem>,
                  ...infoCapitalGoalsIt.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>
                      {item.successFactorName}
                    </MenuItem>
                  )),
                ]}
                {infoCapitalGoalsData.length > 0 && [
                  <MenuItem key="__icd_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                    ── {t("infoCapitalGoalData.list.title")} ──
                  </MenuItem>,
                  ...infoCapitalGoalsData.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>
                      {item.successFactorName}
                    </MenuItem>
                  )),
                ]}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("financialGoal.selectIndicatorHint")}</InputLabel>
              <Select
                multiple
                disabled={sourceIndicators.length === 0}
                value={selectedNames}
                onChange={(e) => setSelectedNames(e.target.value)}
                input={<OutlinedInput label={t("financialGoal.selectIndicatorHint")} />}
              >
                {sourceIndicators.map((ind) => (
                  <MenuItem key={ind.indicatorName} value={ind.indicatorName} disabled={usedIndicatorNames.has(ind.indicatorName)}>
                    {ind.indicatorName}（{ind.unit}）{usedIndicatorNames.has(ind.indicatorName) ? " （登録済み）" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit" disabled={!successFactorName.trim()}>
            {t("common.add")}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/design/${businessDomainId}/strategy-map/org-capital-goals`)}
          >
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
